// PdfGrid.jsx

import React, { useRef } from 'react';
import PropTypes from 'prop-types';

export default function PdfGrid({
    pdfs,
    onOpen,
    isSelected,
    onItemClick,
    onItemContextMenu,
    onSwipeLeft,
    onSwipeRight,
}) {
    const lastTapRef = useRef({ time: 0, id: null });
    const touchStartRef = useRef({ x: 0, y: 0, time: 0 });

    const TAP_DELAY = 300;      // мс для double tap
    const SWIPE_THRESHOLD = 40; // px мінімальна відстань для свайпу

    const handleTouchStart = (e) => {
        if (!e.touches || e.touches.length === 0) return;
        const t = e.touches[0];
        touchStartRef.current = {
            x: t.clientX,
            y: t.clientY,
            time: Date.now(),
        };
    };

    const handleTouchEnd = (doc, idx, e) => {
        if (!e.changedTouches || e.changedTouches.length === 0) return;
        const t = e.changedTouches[0];
        const start = touchStartRef.current;
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;

        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);

        // Якщо це горизонтальний свайп → не вважаємо це тапом
        if (absDx > SWIPE_THRESHOLD && absDx > absDy) {
            if (dx < 0 && onSwipeLeft) {
                // свайп вліво
                onSwipeLeft(doc, idx, e);
            } else if (dx > 0 && onSwipeRight) {
                // свайп вправо
                onSwipeRight(doc, idx, e);
            }
            return;
        }

        // Якщо це не свайп — вважаємо тапом і перевіряємо double tap
        const now = Date.now();
        const { time, id } = lastTapRef.current;

        if (now - time < TAP_DELAY && id === doc.id) {
            // double tap → відкриваємо
            onOpen?.(idx, 'pdf');
            lastTapRef.current = { time: 0, id: null };
        } else {
            // перший тап → виділення / клік по елементу
            onItemClick?.(doc, e);
            lastTapRef.current = { time: now, id: doc.id };
        }
    };

    return (
        <>
            <h3 className="font-medium mb-2">Документи (PDF)</h3>
            <ul className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 mb-6">
                {pdfs.map((doc, idx) => {
                    const sel = isSelected?.(doc);

                    return (
                        <li key={doc.id} className="group">
                            <button
                                // Desktop: звичайний onClick / onDoubleClick
                                onClick={(e) => onItemClick?.(doc, e)}
                                onDoubleClick={() => onOpen?.(idx, 'pdf')}

                                // Mobile: tap + double-tap + swipe
                                onTouchStart={handleTouchStart}
                                onTouchEnd={(e) => handleTouchEnd(doc, idx, e)}

                                onContextMenu={(e) => onItemContextMenu?.(doc, e)}
                                data-sel-key={`pdf:${doc.id}`}
                                aria-selected={isSelected(doc)}
                                className={[
                                    "block w-full aspect-square overflow-hidden rounded-2xl shadow-sm bg-black group-hover:opacity-80 group-hover:brightness-110",
                                    sel ? "ring-2 ring-blue-500 bg-blue-50" : ""
                                ].join(" ")}
                                title={doc.name}
                            >
                                <img
                                    src={doc.thumb || doc.url}
                                    alt={doc.name || 'Зображення'}
                                    loading="lazy"
                                    className="h-full w-full object-cover opacity-80 group-hover:opacity-100"
                                />
                            </button>
                            <div className="mt-1 text-xs truncate text-gray-700" title={doc.name}>
                                {doc.name}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </>
    );
}

PdfGrid.propTypes = {
    pdfs: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        thumb: PropTypes.string,
        url: PropTypes.string,
    })),
    onOpen: PropTypes.func.isRequired,
    isSelected: PropTypes.func.isRequired,
    onItemClick: PropTypes.func.isRequired,
    onItemContextMenu: PropTypes.func.isRequired,

    // 🔽 нові пропси для навігації свайпами (опційні)
    onSwipeLeft: PropTypes.func,
    onSwipeRight: PropTypes.func,
};
